import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Info, Calendar, Clock, RefreshCw, User, MapPin, Monitor, Globe, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import {
  fetchAdminContacts,
  contactTypeLabel,
  type AdminContactRecord,
} from "@/utils/adminContacts";

interface PlanActivation {
  id: string;
  plan: string;
  billing_period: string | null;
  activation_date: string;
  expiration_date: string | null;
  activation_type: string;
  notes: string | null;
  created_at: string;
}

interface UserActivationHistoryProps {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  createdAt: string | null;
  firstActivationDate: string | null;
  lastActivationDate: string | null;
  subscriptionEndDate: string | null;
  activationCount: number | null;
}

const UserActivationHistory: React.FC<UserActivationHistoryProps> = ({
  userId,
  userName,
  userEmail,
  createdAt,
  firstActivationDate,
  lastActivationDate,
  subscriptionEndDate,
  activationCount
}) => {
  const [activations, setActivations] = useState<PlanActivation[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [contacts, setContacts] = useState<AdminContactRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Não disponível";
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const formatDateShort = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(dateString));
  };

  const fetchActivationHistory = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('plan_activations')
        .select('*')
        .eq('user_id', userId)
        .order('activation_date', { ascending: false });

      if (error) {
        console.error('Erro ao buscar histórico de ativações:', error);
        return;
      }

      setActivations(data || []);
    } catch (err) {
      console.error('Erro:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('user_sessions' as any)
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!error && data) {
        setSessions(data as any[]);
      }
    } catch (err) {
      console.error('Erro ao buscar sessões:', err);
    }
  };

  const loadContacts = async () => {
    const data = await fetchAdminContacts(userId);
    setContacts(data);
  };

  useEffect(() => {
    if (isOpen) {
      fetchActivationHistory();
      fetchSessions();
      loadContacts();
    }
  }, [isOpen, userId]);

  const getActivationTypeBadge = (type: string) => {
    switch (type) {
      case 'manual':
        return <Badge variant="outline" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Manual</Badge>;
      case 'payment':
        return <Badge variant="outline" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Pagamento</Badge>;
      case 'renewal':
        return <Badge variant="outline" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">Renovação</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const getPlanBadge = (plan: string) => {
    const colors: { [key: string]: string } = {
      'premium': 'bg-blue-500',
      'vip': 'bg-purple-500',
      'quarterly': 'bg-gold-500',
      'annual': 'bg-emerald-500',
      'basic': 'bg-green-500',
      'free': 'bg-gray-500'
    };
    return (
      <Badge className={colors[plan] || 'bg-gray-500'}>
        {plan?.charAt(0).toUpperCase() + plan?.slice(1)}
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="text-blue-500 hover:text-blue-600" title="Ver histórico de ativações">
          <Info size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Histórico de Ativações
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Informações do Usuário */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Dados do Cliente</h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{userName || 'Sem nome'}</span>
              </div>
              <div className="text-muted-foreground">{userEmail}</div>
            </div>
          </div>

          {/* Informações de Conexão */}
          <div className="bg-muted/30 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Informações de Conexão
            </h4>
            {sessions.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {sessions.map((s: any, idx: number) => (
                  <div key={s.id || idx} className="border rounded-lg p-3 text-sm space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="w-3 h-3" />
                      <span className="font-mono text-xs">{s.ip_address || 'IP desconhecido'}</span>
                      <span className="ml-auto text-xs">{formatDate(s.created_at)}</span>
                    </div>
                    {(s.city || s.state || s.country) && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span className="text-xs">
                          {[s.city, s.state, s.country].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Monitor className="w-3 h-3" />
                      <span className="text-xs">{s.device || 'Dispositivo desconhecido'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <Globe className="w-6 h-6 mx-auto mb-2 opacity-50" />
                <p className="text-xs">Nenhuma sessão registrada ainda.</p>
                <p className="text-xs mt-1">Os dados de IP, cidade e dispositivo aparecerão após o próximo login do usuário.</p>
              </div>
            )}
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-sm text-blue-800 dark:text-blue-200 uppercase tracking-wide flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Resumo de Datas
            </h4>
            
            <div className="grid grid-cols-1 gap-3 text-sm">
              <div className="flex justify-between items-center p-2 bg-white/50 dark:bg-white/5 rounded">
                <span className="text-muted-foreground">📅 Data de Cadastro:</span>
                <span className="font-medium">{formatDateShort(createdAt)}</span>
              </div>
              
              <div className="flex justify-between items-center p-2 bg-white/50 dark:bg-white/5 rounded">
                <span className="text-muted-foreground">🎯 Primeira Ativação:</span>
                <span className="font-medium">{formatDateShort(firstActivationDate)}</span>
              </div>
              
              <div className="flex justify-between items-center p-2 bg-white/50 dark:bg-white/5 rounded">
                <span className="text-muted-foreground">🔄 Última Ativação:</span>
                <span className="font-medium">{formatDateShort(lastActivationDate)}</span>
              </div>
              
              <div className="flex justify-between items-center p-2 bg-white/50 dark:bg-white/5 rounded">
                <span className="text-muted-foreground">⏰ Encerramento do Plano:</span>
                <span className="font-medium text-orange-600 dark:text-orange-400">
                  {formatDateShort(subscriptionEndDate)}
                </span>
              </div>
              
              <div className="flex justify-between items-center p-2 bg-gold-100/50 dark:bg-gold-900/20 rounded border border-gold-200 dark:border-gold-700">
                <span className="text-gold-700 dark:text-gold-300 flex items-center gap-1">
                  <RefreshCw className="w-4 h-4" />
                  Total de Ativações/Renovações:
                </span>
                <span className="font-bold text-gold-800 dark:text-gold-200 text-lg">
                  {activationCount || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Histórico de Contatos enviados pelo admin */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Histórico de Contatos
            </h4>

            {contacts.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {contacts.map((c, idx) => (
                  <div key={c.id || idx} className="border rounded-lg p-3 text-sm flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                        {c.channel === 'whatsapp' ? 'WhatsApp' : c.channel}
                      </Badge>
                      <span className="text-xs">{contactTypeLabel(c.contact_type)}</span>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(c.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground bg-muted/30 rounded-lg">
                <MessageCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
                <p className="text-xs">Nenhum contato registrado ainda.</p>
                <p className="text-xs mt-1">As mensagens enviadas pelo painel passam a ficar registradas aqui.</p>
              </div>
            )}
          </div>

          {/* Histórico de Ativações */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Histórico Detalhado
            </h4>
            
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : activations.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {activations.map((activation, index) => (
                  <div 
                    key={activation.id} 
                    className="border rounded-lg p-3 text-sm hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        {getPlanBadge(activation.plan)}
                        {getActivationTypeBadge(activation.activation_type)}
                      </div>
                      <span className="text-xs text-muted-foreground">#{activations.length - index}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                      <div>Ativado: {formatDateShort(activation.activation_date)}</div>
                      <div>Expira: {formatDateShort(activation.expiration_date)}</div>
                    </div>
                    {activation.notes && (
                      <div className="mt-2 text-xs italic text-muted-foreground border-t pt-2">
                        {activation.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground bg-muted/30 rounded-lg">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Nenhuma ativação registrada ainda.</p>
                <p className="text-xs mt-1">Ativações futuras aparecerão aqui.</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserActivationHistory;
